import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";
import { APIMemberData } from "@companion-core/shared/app/Interfaces/API";
import {
  getAvailableFields,
  getCountriesOptions,
  getOrganizationSizes,
  PersonalInformationPayload,
  PhoneInfos,
} from "@companion-core/shared/app/Components/MyAccount/Sections/PersonalInformation/PersonalInformation.model";
import { useMemberStore } from "@companion-core/shared/app/Store/memberStore";
import { industryItems } from "@companion-core/shared/app/Constants/organization";
import { useUpdateMember } from "@companion-core/shared/app/Queries";
import { getModule } from "@companion-core/shared/app/Utils/config";

export const usePersonalInformation = () => {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const isMemberFetching = useIsFetching({ queryKey: ["member"] });
  const data = queryClient.getQueryData(["member"]) as APIMemberData | null;
  const myAccountModule = getModule("my-account");
  const { setValidationStatus, setValidationMessage, resetValidationBanner } = useMemberStore();
  const [phoneDisplayed, setPhoneDisplayed] = useState<PhoneInfos[]>([]);
  const [validationFunctions, setValidationFunctions] = useState<
    Record<string, () => void | undefined>
  >({});

  const personalInformationConfig = useMemo(() => myAccountModule?.modules?.information, []);
  const availablePhonesField = useMemo(
    () => getAvailableFields("phones", personalInformationConfig),
    [personalInformationConfig]
  );
  const availableAddressesField = useMemo(
    () => getAvailableFields("addresses", personalInformationConfig),
    [personalInformationConfig]
  );

  const registerValidation = useCallback((name: string, validateFn: () => void) => {
    setValidationFunctions((prev) => ({ ...prev, [name]: validateFn }));
  }, []);

  const { mutateAsync: updateMember, isPending } = useUpdateMember(() => {
    setValidationStatus("error");
    setValidationMessage(t("my-account.personal-information.error"));
  });

  const countriesOptions = getCountriesOptions().filter((item) => i18n.exists(item.text));
  const organizationSizesOptions = getOrganizationSizes();

  const organizationOptions = industryItems.map((value) => ({
    text: t(`common.industry.${value}`),
    value,
  }));

  const validateAllFields = () => {
    Object.values(validationFunctions).forEach((validateFn) => {
      if (validateFn) {
        validateFn();
      }
    });
  };

  const onSubmitForm = async (payload: Record<string, unknown>) => {
    resetValidationBanner();
    if (!data) return;

    const formattedPhones = availablePhonesField.reduce(
      (acc, phoneType) => ({ ...acc, [phoneType]: payload[`${phoneType}`] ?? " " }),
      {}
    );

    const formattedAddresses: APIMemberData["addresses"] = [];
    availableAddressesField.forEach((addressType) => {
      if (payload[`${addressType}-street`]) {
        formattedAddresses.push({
          street: payload[`${addressType}-street`] as string,
          "zip_code": payload[`${addressType}-zip_code`] as string,
          "country_code": payload[`${addressType}-country_code`] as string,
          city: payload[`${addressType}-city`] as string,
          type: addressType,
          "address_complement": null,
          "state": null,
        });
      }
    });

    const formattedPayload: PersonalInformationPayload = {
      ...formattedPhones,
      addresses: formattedAddresses,
      organization: {
        size: (payload["organization-size"] as string) ?? null,
        name: (payload["organization-name"] as string) ?? null,
        industry: (payload["organization-industry"] as string) ?? null,
      },
    };

    try {
      await updateMember({ id: "me", payload: formattedPayload });
      queryClient.invalidateQueries({ queryKey: ["member"] });
      setValidationStatus("success");
      setValidationMessage(t("my-account.personal-information.success"));
    } catch {
      setValidationStatus("error");
      setValidationMessage(t("my-account.personal-information.error"));
    }
  };

  return {
    t,
    isMemberFetching,
    data,
    personalInformationConfig,
    phoneDisplayed,
    setPhoneDisplayed,
    availablePhonesField,
    registerValidation,
    countriesOptions,
    organizationSizesOptions,
    organizationOptions,
    validateAllFields,
    validationFunctions,
    setValidationFunctions,
    onSubmitForm,
    isPending,
  };
};
